import { useCallback, useRef, useState } from "react";
import { X, Trash2 } from "lucide-react";

/**
 * Returns [confirm, ConfirmModal].
 *
 * Usage:
 *   const [confirm, ConfirmModal] = useConfirm();
 *   // in JSX: <ConfirmModal />
 *   // to open: if (!(await confirm("Are you sure?"))) return;
 */
export function useConfirm() {
  const [state, setState] = useState(null); // { message, resolve }
  const resolveRef = useRef(null);

  const confirm = useCallback((message) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ message });
    });
  }, []);

  function handle(result) {
    setState(null);
    resolveRef.current?.(result);
    resolveRef.current = null;
  }

  function ConfirmModal() {
    if (!state) return null;
    return (
      <div
        className="fixed inset-0 z-[100] bg-black/75 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={() => handle(false)}
      >
        <div
          className="w-full max-w-sm bg-axe-surface border border-axe-border rounded-2xl p-6 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-red-500/15 border border-red-500/30">
              <Trash2 size={18} className="text-red-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white leading-relaxed">{state.message}</p>
              <p className="mt-1 text-xs text-neutral-500">This action cannot be undone.</p>
            </div>
            <button
              onClick={() => handle(false)}
              className="flex-shrink-0 p-1 text-neutral-500 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X size={16} />
            </button>
          </div>
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => handle(false)}
              className="flex-1 rounded-full border border-axe-border px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-bold text-neutral-300 hover:bg-axe-bg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => handle(true)}
              className="flex-1 rounded-full bg-red-600 px-4 py-2.5 text-xs uppercase tracking-[0.22em] font-bold text-white hover:bg-red-500 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  }

  return [confirm, ConfirmModal];
}
